<template>
	<div class="d-flex align-items-stretch px-4 w-100">
		<b-col
			class="text-center d-flex flex-column align-items-center justify-content-between my-4"
			cols="3"
		>
			<span class="chart-label"
				>Question Composition ({{ this.url }} based)</span
			>
			<div :id="`legend-${this.url}`"></div>
		</b-col>
		<b-col
			cols="7"
			offset="1"
			:id="`pie-chart-${this.url}`"
			class="d-flex justify-content-center"
		></b-col>
	</div>
</template>

<script>
import { select } from "d3-selection";
import { arc, pie } from "d3-shape";
import { scaleOrdinal } from "d3-scale";

import { getPieData } from "../../../utils/apiFunctions";

export default {
	name: "PieChart",
	data() {
		return {
			pieData: [
				{ name: "1", value: 3 },
				{ name: "2", value: 2 },
				{ name: "3", value: 5 },
				{ name: "4", value: 2 },
				{ name: "5", value: 5 },
			],

			colors: ["#FFD572", "#7AD3FF", "#FE6C6C", "#FD95D3", "#99FFA3"],
		};
	},

	props: [
		"url", // This takes the end of the url endpoint to get the data
		"testId", //Id of the test needed to send HTTP GET request for data
	],

	async mounted() {
		try {
			const response = await this.getData();
			this.pieData = response.data;
			this.createChart();
		} catch (e) {
			console.error("Error getting data", e);
		}
	},
	methods: {
		async getData(filter = null) {
			const { data } = await getPieData(this.url, this.testId, filter);
			if (data.type !== "Success") {
				throw new Error("Request Failed");
			} else {
				return data;
			}
		},

		async getDataOnClick(data) {
			if (this.url === "section") {
				await this.getData(data.id);
			} else {
				await this.getData(data.name);
			}
		},

		createChart() {
			const size = 600;

			const svg = select(`#pie-chart-${this.url}`)
				.append("svg")
				.attr("viewBox", `0 0 ${size} ${size}`);
			const graph = svg
				.append("g")
				.attr("transform", `translate(${size / 2}, ${size / 2})`);

			const arcGenerator = arc()
				.innerRadius(0)
				.outerRadius(size / 3);
			const colorGenerator = scaleOrdinal()
				.domain(this.pieData)
				.range(this.colors);
			const pieDataGenerator = pie().value((data) => data.value);
			const generatedData = pieDataGenerator(this.pieData);

			generatedData.forEach((data, index) => {
				data.scale = 0.95 + (index + 1) * 0.05;
			});
			graph
				.selectAll("path")
				.data(generatedData)
				.enter()
				.append("path")
				.attr("d", arcGenerator)
				.attr("fill", (d) => colorGenerator(d.data.name))
				.attr("transform", (d) => `scale(${d.scale})`)
				.attr("stroke", "#FFFFFF")
				.style("stroke-width", 3)
				.style("opacity", 0.7)
				.classed("arc", true)
				.on("click", (_, data) => this.getDataOnClick(data.data))
				.append("title")
				.text(
					(d) => `${d.data.value} questions of ${this.url} (${d.data.name})`
				);

			graph
				.append("circle")
				.attr("r", size / 4)
				.attr("fill", "#FFFFFF");

			// Calculating the total questions
			let total = 0;
			this.pieData.forEach((data) => (total += data.value));
			const text = graph
				.append("text")
				.attr("font-family", '"Poppins", sans-serif')
				.attr("text-anchor", "middle");
			text
				.append("tspan")
				.attr("x", 0)
				.text(total)
				.attr("font-size", 44);
			text
				.append("tspan")
				.attr("x", 0)
				.attr("dy", 30)
				.text("Total")
				.attr("font-size", 23);

			// Legend population
			const legend = select(`#legend-${this.url}`);
			legend
				.selectAll("div")
				.data(this.pieData)
				.enter()
				.append("div")
				.attr("class", "legend-item")
				.append("div")
				.attr("class", "legend-color")
				.style("background-color", (d) => colorGenerator(d.name));

			legend
				.selectAll(".legend-item")
				.data(this.pieData)
				.append("span")
				.classed("text-capitalize", true)
				.text(
					(d) =>
						`${this.url} ${
							d.name.toString().length < 12
								? d.name
								: d.name.toString().substr(0, 10) + "..."
						}`
				);
		},
	},
};
</script>

<style>
.chart-label {
	font-size: var(--size-14);
	color: #72738e;
}

.legend-item {
	display: flex;
	align-items: center;
	margin-bottom: 10px;
}

.legend-color {
	width: 20px;
	height: 20px;
	border-radius: 50%;
	display: inline-block;
	margin: 0 10px;
}
</style>
